"use client"
import FilterDDIcon from "@/assets/images/Common/FilterDDIcon.svg"
import FilterIcon from "@/assets/images/Common/FilterIcon.svg"
import FlagIcon from "@/assets/images/Common/FlagIcon.svg"
import HomeIcon from "@/assets/images/Common/HomeIcon.png"
import OwnerStaysIcon from "@/assets/images/Common/OwnerStays.svg"
import BooksSpread from "@/assets/images/CounterCards/BooksSpread.svg"
import Calculator from "@/assets/images/CounterCards/Calculator.svg"
import ManSitting from "@/assets/images/CounterCards/ManSitting.svg"
import FilterPopover from "@/components/Common/AdvancedFilter/FilterPopover"
import CounterCards from "@/components/Common/CounterCards/CounterCard"
import CounterCardFilter from "@/components/Common/CounterCards/CounterCardFilter"
import CustomAvatar from "@/components/Common/CustomAvatar"
import CustomTooltip from "@/components/Common/CustomTooltip"
import InfiniteTable from "@/components/Common/InfiniteTable/InfiniteTable"
import { FilterSortExtractor, calculateToFromFilter } from "@/components/Common/InfiniteTable/InfiniteTableExt"
import useInfiniteTable from "@/components/Common/InfiniteTable/useInfiniteTable"
import API_END_POINT, { ROUTES } from "@/utils/api/apiEndpoints"
import { apiClient } from "@/utils/api/apiService"
import { cryptoGenerator } from "@/utils/functions/CommonFunction"
import useProperties from "@/utils/hooks/useProperties"
import useRoles from "@/utils/hooks/useRoles"
import useUserDetails from "@/utils/hooks/useUserDetails"
import AppStore from "@/utils/store/AppStore"
import { ModuleName, TAPIPayload } from "@/utils/store/AppStore.types"
import { COLORS } from "@/utils/theme/colors"
import { ArrivalDepartureTicket, ItemsEntity } from "@/utils/types/ArrivalDepartureTicket"
import { ExtendedGridColDef, filterInt } from "@/utils/types/InfiniteTable"
import { TInternalEmployees } from "@/utils/types/InternalEmployees"
import { UserDetails } from "@/utils/types/UserDetails"
import { Check } from "@mui/icons-material"
import Close from "@mui/icons-material/Close"
import VisibilityIcon from "@mui/icons-material/Visibility"
import { Box, Button, Checkbox, Chip, ChipProps, FormControlLabel, IconButton, SxProps, Typography } from "@mui/material"
import { GridRenderCellParams } from "@mui/x-data-grid"
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import { useInfiniteQuery, useQuery } from "@tanstack/react-query"
import dayjs from "dayjs"
import Image from "next/image"
import Link from "next/link"
import { useCallback, useMemo, useRef, useState } from "react"
import { toJS } from "mobx"

const sortIdMap: filterInt = {
  scheduled_date: "scheduled_date",
  services_type: "services_type",
  current_status: "current_status",
  reservation: "reservation_id",
  property_details: "property_code",
  booking_date: "arrival_date",
  completed_date: "completed_date",
}

function arrivalAndDepartureReviewStatus(params: GridRenderCellParams): ChipProps {
  const isReviewed = params?.row?.is_reviewed
  const label = isReviewed ? "Yes" : "No"
  const icon = isReviewed ? <Check style={{ color: "white" }} /> : <Close style={{ color: "white" }} />

  if (isReviewed === true) {
    return {
      label: label,
      style: {
        backgroundColor: COLORS.brandBlue,
        border: "none",
      },
      icon,
    }
  } else {
    return {
      label: label,
      style: {
        backgroundColor: COLORS.red,
        border: "none",
      },
      icon,
    }
  }
}

const Listing = () => {
  const [selectedOptions, setSelectedOptions] = useState<{
    ticket_id: string
    type: string
    pdf_type?: "guest" | "admin" | "owner"
    services_type?: string
  } | null>(null)
  const filterAnchorDebugref = useRef(null)
  const [filterPopper, setFilterPopper] = useState<boolean>(false)
  const { filterRef, sortRef, isNextPage, infiniteTableKey, queryClient, tableAPIRef, resetQuery } = useInfiniteTable(ModuleName.AND)
  const { data: userDetailsData } = useUserDetails()
  const { isOwner } = useRoles(userDetailsData?.data as UserDetails)

  const { data: properties, isFetched: isFetchedProperty } = useProperties({ enabled: isOwner })

  const propertyList = useMemo(() => {
    if (!properties?.data) {
      return []
    }

    return properties.data
      .sort((a, b) => String(a.property_code).localeCompare(String(b.property_code)))
      .map((item) => ({
        alias: item?.property_code,
        value: item?.property_code,
        label: `${item?.property_code} (${item?.property_name})`,
      }))
  }, [properties])

  const { data: andCounter, refetch: refetchAnDCounter } = useQuery<any>({
    queryKey: ["andCount"],
    queryFn: async () => {
      const prevFilters = calculateToFromFilter(AppStore.counterCardFilter[infiniteTableKey])
      return await apiClient.post(API_END_POINT.POST[ROUTES.getaArrivalDepartureCountv2], { ...prevFilters })
    },
  })

  const { data: internalEmployeesData, isFetched: isFetchedEmployees } = useQuery<{
    data: TInternalEmployees
  }>({
    queryKey: ["internalEmployees"],
    queryFn: async () => {
      return await apiClient.get(API_END_POINT.GET[ROUTES.getInternalEmployees])
    },
  })

  const internalEmpList = useMemo(() => {
    if (!internalEmployeesData?.data?.data) {
      return []
    }

    const tempEmp = internalEmployeesData?.data?.data.map((item) => ({
      alias: item?.Full_Name,
      value: item?.id,
      label: item?.Full_Name,
    }))
    return [{ label: "Unassinged", value: "", alias: "Unassinged" }, ...tempEmp]
  }, [internalEmployeesData])

  const [totalNumberOfTickets, setTotalNumberOfTickets] = useState<number>(0)

  const postArrivalDepartureTicket = async (pageParam: number) => {
    const data = FilterSortExtractor({
      page: pageParam,
      tableRef: tableAPIRef.current.rootElementRef,
      filterRef,
      sortRef,
      filterFlag: true,
      perPage: "calculate",
    })

    const res: ArrivalDepartureTicket = await apiClient.post(`${API_END_POINT.POST[ROUTES.getPostArrivalDepartureTicketv2]}`, {
      ...data,
      hide_inactive: AppStore.isHideInactiveTickets[infiniteTableKey].isTrue,
    })
    isNextPage.current = res.data.nextPage !== null

    setTotalNumberOfTickets(res.data.itemsTotal || 0)
    return {
      data: res,
    }
  }

  const { data: arrDeptTicketData, ...arrDeptRest } = useInfiniteQuery({
    queryKey: [infiniteTableKey],
    queryFn: ({ pageParam = 1 }) => postArrivalDepartureTicket(pageParam),
    initialPageParam: 1,
    getNextPageParam: (lastPage, pages) => lastPage?.data?.data?.nextPage || 1,
  })

  const columns: Array<ExtendedGridColDef> = [
    {
      field: "reservation",
      filterKey: "hostaway_reservation_id",
      sortKey: "reservation_id",
      headerName: "Reservation ID",
      sortable: true,
      filterable: false,
      renderCell: (params) => {
        const isOwner = params?.row?.reservation?.status === "ownerStay"
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: "10px", justifyContent: "center", width: "max-content", position: "relative" }}>
            {isOwner && (
              <CustomTooltip title={"Owner Stay"}>
                <Image
                  src={OwnerStaysIcon}
                  alt={"Owner"}
                  width={20}
                  height={20}
                  style={{ position: "absolute", left: "-30px", top: "50%", transform: "translateY(-50%)" }}
                />
              </CustomTooltip>
            )}
            <div>{params.value?.hostawayReservationId}</div>
          </Box>
        )
      },
      headerAlign: "center",
      align: "center",
      minWidth: 150,
      flex: 1,
    },
    {
      field: "property_details",
      filterKey: "property_code",
      sortKey: "property_code",
      headerName: "Property Code",
      filterable: false,
      sortable: true,
      type: isOwner ? "singleSelect" : undefined,
      // @ts-ignore
      valueOptions: isOwner ? propertyList : undefined,
      renderCell: (params) => {
        const propertyName = params?.value?.property_name || ""
        const truncatedName = propertyName.length > 10 ? `${propertyName.substring(0, 16)}...` : propertyName

        return (
          <Box display={"flex"} flexDirection={"column"}>
            <CustomTooltip
              title={
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    flexDirection: "column",
                  }}
                >
                  <div>
                    <b>Property Name:</b> {propertyName}
                  </div>
                  <div>
                    <b>Address:</b> {params?.value?.address}
                  </div>
                </div>
              }
            >
              <div>{params?.value?.property_code}</div>
            </CustomTooltip>
          </Box>
        )
      },
      headerAlign: "center",
      align: "center",
      minWidth: 250,
      flex: 1,
    },
    {
      field: `services_type`,
      filterKey: "services_type",
      sortKey: "services_type",
      headerName: "Event Type",
      // flex: 1,
      sortable: true,
      filterable: false,
      type: "singleSelect",
      valueOptions: [
        { value: "arrival", label: "Arrival", alias: "Arrival" },
        { value: "departure", label: "Departure", alias: "Departure" },
      ],
      renderCell: (params) => {
        return <Chip variant="outlined" size="small" {...getChipProps(params)} />
      },
      headerAlign: "center",
      align: "center",
      minWidth: 100,
      flex: 1,
    },
    {
      field: "ticket_status",
      filterKey: isOwner ? "assigned_to_id" : "assigned_to",
      sortKey: "assigned_to",
      headerName: "Assigned To",
      headerAlign: "center",
      type: isOwner ? "singleSelect" : "string",
      // @ts-ignore
      valueOptions: isOwner ? internalEmpList : undefined,
      filterable: false,
      align: "center",
      sortable: true,
      renderCell: (params) => {
        return (
          <>
            {params?.value?.assigned_to?.Full_Name ? (
              <CustomTooltip title={params?.value?.assigned_to?.Full_Name}>
                <CustomAvatar name={params?.value?.assigned_to?.Full_Name} />
              </CustomTooltip>
            ) : (
              <CustomTooltip title={"Unassigned"}>
                <CustomAvatar name={"UN"} unknown={true} />
              </CustomTooltip>
            )}
          </>
        )
      },
      minWidth: 250,
      flex: 1,
    },
    {
      field: "current_status",
      // filterKey: "current_status",
      sortKey: "current_status",
      headerName: "Status",
      sortable: true,
      filterable: false,
      type: "singleSelect",
      valueOptions: [
        { value: "Open", label: "New / Open", alias: "New / Open" },
        { value: "In-progress", label: "In-Progress" },
        { value: "Scheduled", label: "Scheduled" },
        { value: "Completed", label: "Completed" },
      ],
      renderCell: (params) => {
        return <Chip variant="outlined" size="small" {...getChipProps(params)} />
      },
      headerAlign: "center",
      align: "center",
      minWidth: 150,
      flex: 1,
    },
    {
      field: `booking_date`,
      headerName: "Arr / Dep Date",
      description: "Arrival / Departure Date",
      type: "date",
      sortable: true,
      filterable: false,
      valueGetter: (params) => new Date(params.value),
      renderCell: (params) => {
        const isArrival = params.row.services_type === "arrival"
        const broadText: SxProps = { fontSize: "16px", fontWeight: 500 }
        const smallText: SxProps = { fontSize: "12px", fontWeight: 400, color: "rgba(255, 255, 255, 0.70)" }
        const arrDayJs = dayjs(params.row.reservation?.arrivalDate, "YYYY-MM-DD").format("DD-MM-YYYY")
        const depDayJs = dayjs(params.row.reservation?.departureDate, "YYYY-MM-DD").format("DD-MM-YYYY")
        return (
          <CustomTooltip
            title={
              <div>
                <div>
                  <b>Arrival Date :</b> {arrDayJs}
                </div>
                <div>
                  <b>Departure Date :</b> {depDayJs}
                </div>
              </div>
            }
          >
            <Box sx={{ textAlign: "center" }}>
              <Box sx={isArrival ? broadText : smallText}>{arrDayJs}</Box>
              <Box sx={isArrival ? smallText : broadText}>{depDayJs}</Box>
            </Box>
          </CustomTooltip>
        )
      },
      headerAlign: "center",
      align: "center",
      minWidth: 200,
      flex: 1,
    },
    {
      field: "scheduled_date",
      filterKey: "scheduled_date_filter",
      outputFormat: "localOffsetTS",
      sortKey: "scheduled_date",
      headerName: "Due Date",
      // flex: 1,
      type: "date",
      sortable: true,
      filterable: false,
      valueGetter: (params) => new Date(params.value),
      renderCell: (params) => {
        const timestamp = params?.value

        // Create a Date object from the timestamp
        const date = new Date(timestamp)

        // Extract day, month, and year components
        const day = date.getDate()
        const month = date.getMonth() + 1 // Months are zero-based, so add 1
        const year = date.getFullYear()

        // Format the date components as strings with leading zeros if necessary
        const formattedDay = day < 10 ? `0${day}` : `${day}`
        const formattedMonth = month < 10 ? `0${month}` : `${month}`
        const formattedYear = year < 10 ? `0${year}` : `${year}`

        // Create the formatted date string in "dd/mm/yy" format
        const formattedDate = `${formattedDay}-${formattedMonth}-${formattedYear}`

        return (
          <>
            <Box display="flex" flexDirection="row" justifyContent="center" textAlign="center">
              {/* <Typography>{params?.value?.ticket_number}</Typography> */}
              {/* <Typography fontSize="0.8rem" color={COLORS?.adminAppTextPrimary}>
                {formattedDate}
              </Typography> */}
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  value={dayjs(formattedDate, "DD-MM-YYYY")}
                  readOnly
                  format="DD-MM-YYYY"
                  sx={{
                    width: "100px",
                    "& fieldset": {
                      border: "none",
                    },
                    "& input": {
                      padding: 0,
                      margin: 0,
                      fontWeight: 400,
                      lineHeight: 1.5,
                      letterSpacing: "0.00938em",
                      fontSize: "0.875rem",
                      color: "#E7E3FC",
                      textAlign: "center",
                    },
                    "& .MuiInputAdornment-positionEnd": {
                      marginLeft: 0,
                      button: {
                        padding: 0,
                      },
                    },
                  }}
                  disableOpenPicker
                />
              </LocalizationProvider>
            </Box>
          </>
        )
      },
      headerAlign: "center",
      align: "center",
      minWidth: 150,
      flex: 1,
    },
    {
      field: "completed_date",
      filterKey: "completed_date_filter",
      sortKey: "completed_date",
      headerName: "Completed Date",
      type: "date",
      sortable: true,
      filterable: false,
      valueGetter: (params) => new Date(params.value),
      renderCell: (params) => {
        const isCompleted = params.row?.current_status === "Completed"
        const completedAt = params?.row?.ticket_status?.completed_date
        const arrDayJs = isCompleted && completedAt ? dayjs(completedAt).format("DD-MM-YYYY") : ""

        return <Box sx={{ textAlign: "center" }}>{arrDayJs}</Box>
      },
      headerAlign: "center",
      align: "center",
      minWidth: 150,
      flex: 1,
    },
    {
      field: "owner_stays",
      filterKey: "is_owner_stay",
      headerName: "Owner Stays",
      filterable: false,
      sortable: false,
      type: "singleSelect",
      valueOptions: [
        { value: "Yes", label: "Yes" },
        { value: "No", label: "No" },
      ],
      renderCell: (params) => {
        const isOwner = params?.row?.reservation?.status === "ownerStay"
        return (
          <Box textAlign={"center"} width="100%">
            {isOwner ? "Yes" : "No"}
          </Box>
        )
      },
    },
    {
      field: "guest_issue",
      filterKey: "has_guest_issue",
      headerName: "Guest Issue",
      description: "Guest Completed Issue",
      filterable: false,
      sortable: false,
      type: "singleSelect",
      valueOptions: [
        { value: "Yes", label: "Yes" },
        { value: "No", label: "No" },
      ],
      renderCell: (params) => {
        const isCompleted = params.row?.current_status === "Completed"
        const guestCompletedIssue = isCompleted && params?.row?.guest_depart_issue === "Yes"
        return (
          <Box textAlign={"center"} width="100%">
            {guestCompletedIssue ? "Yes" : isCompleted ? "No" : ""}
          </Box>
        )
      },
    },
    {
      field: "arrival_date",
      filterKey: "arrival_date_filter",
      outputFormat: "localOffsetTS",
      sortKey: "arrival_date",
      headerName: "Arrival Date",
      type: "date",
      sortable: false,
      filterable: false,
      headerAlign: "center",
      minWidth: 150,
      valueGetter: (params) => new Date(params.value),
      renderCell: (params) => {
        const { arrivalDate } = params.row.reservation
        const arrDayJs = dayjs(arrivalDate, "YYYY-MM-DD").format("DD-MM-YYYY")
        return <Box sx={{ textAlign: "center", width: "100%" }}>{arrDayJs}</Box>
      },
    },
    {
      field: "departure_date",
      filterKey: "departure_date_filter",
      outputFormat: "localOffsetTS",
      sortKey: "departure_date",
      headerName: "Departure Date",
      type: "date",
      sortable: false,
      filterable: false,
      headerAlign: "center",
      minWidth: 150,
      valueGetter: (params) => new Date(params.value),
      renderCell: (params) => {
        const { departureDate } = params.row.reservation
        const depDayJs = dayjs(departureDate, "YYYY-MM-DD").format("DD-MM-YYYY")
        return <Box sx={{ textAlign: "center", width: "100%" }}>{depDayJs}</Box>
      },
    },
    {
      field: "reviewed",
      //filterKey: "is_reviewed",
      sortKey: "reviewed",
      headerName: "Reviewed",
      type: "singleSelect",
      //filterable: false,
      sortable: true,
      valueOptions: [
        { value: "true", label: "Yes", alias: "Yes" },
        { value: "false", label: "No", alias: "No" },
      ],
      renderCell: (params) => {
        return <Chip variant="outlined" size="small" sx={{ color: "white" }} {...arrivalAndDepartureReviewStatus(params)} />
      },
      align: "center",
      headerAlign: "center",
      minWidth: 150,
      flex: 1,
    },
    {
      field: "actions",
      headerName: "Actions",
      filterable: false,
      sortable: false,
      renderCell: (params) => {
        const guestCompletedIssue = params.row?.current_status === "Completed" && params?.row?.guest_depart_issue?.toLowerCase() === "yes"
        const data = { id: params.row.id, type: params.row.services_type }
        const cipherParams = cryptoGenerator(data, true)
        const isNotRequired = params?.row?.not_required_flag
        const isReviewed = params.row.is_reviewed
        const priority = params.row.priority || "NA"
        return (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
            <CustomTooltip
              title={guestCompletedIssue ?
                <div style={{ textAlign: "center", fontSize: "12px" }}>
                  {params?.row?.departure_issue || 'Guest Departure Completed with an Issue'}
                </div>
                : null}
            >
              <Box sx={{ padding: "8px", display: "flex", justifyContent: "center", alignItems: "center" }}>
                <Image src={FlagIcon} alt="Flag Icon" style={{ visibility: guestCompletedIssue ? "inherit" : "hidden" }} width={24} height={24} />
              </Box>
            </CustomTooltip>
            {priority ? (
              <CustomTooltip title={priority === "ND" ? "Next Day" : priority === "BB" ? "Back to Back" : ""}>
                <CustomAvatar
                  name={priority}
                  width={24}
                  height={24}
                  fontSize={"12px"}
                  color={priority === "NA" || priority === "NR" ? "transparent" : "white"}
                  bgColor={priority === "ND" ? "#e57a2d" : priority === "BB" ? "#0e74ec" : "transparent"}
                />
              </CustomTooltip>
            ) : null}

            <CustomTooltip title="View Ticket">
              <IconButton key={"eye-button"}>
                <Link href={`/arrival-departure/details?aid=${cipherParams}`} style={{ height: "24px" }}>
                  <VisibilityIcon />
                </Link>
              </IconButton>
            </CustomTooltip>
          </Box>
        )
      },
      headerAlign: "center",
      align: "center",
      width: 200,
    },
    {
      field: "priority",
      filterKey: "priority_type_filter",
      headerName: "Priority",
      sortable: false,
      filterable: false,
      type: "singleSelect",
      valueOptions: [
        { value: "BB", label: "Back to Back" },
        { value: "NA", label: "Not Applicable" },
      ],
      renderCell: (params) => {
        const priority = params.row?.priority_overridden ? params.row.priority_type : params.row.priority
        return (
          <Box textAlign={"center"} width="100%">
            {priority}
          </Box>
        )
      },
    },
  ]

  const getChipProps = (params: GridRenderCellParams): ChipProps => {
    var isRead = params.row.read
    if (params.value === "Open") {
      return {
        label: isRead ? "Open" : "New",
        style: {
          backgroundColor: isRead ? COLORS.adminAppBgColorV2 : COLORS.newChipRed,
          color: COLORS.adminAppTextSecondary,
          border: "none",
          width: "100px",
        },
      }
    } else if (params.value === "Scheduled") {
      return {
        label: params.value,
        style: {
          backgroundColor: COLORS.lightGreen,
          color: COLORS.adminAppTextSecondary,
          border: "none",
          width: "100px",
        },
      }
    } else if (params.value === "In-progress") {
      return {
        label: params.value,
        style: {
          backgroundColor: COLORS.blue,
          color: COLORS.adminAppTextSecondary,
          border: "none",
          width: "100px",
        },
      }
    } else if (params.value === "arrival") {
      return {
        label: "Arrival",
        style: {
          backgroundColor: "#6d0093",
          color: COLORS.adminAppTextSecondary,
          border: "none",
          width: "100px",
        },
      }
    } else if (params.value === "departure") {
      return {
        label: "Departure",
        style: {
          backgroundColor: COLORS.darkGreen,
          color: COLORS.adminAppTextSecondary,
          border: "none",
          width: "100px",
        },
      }
    } else if (params.value === "Completed") {
      return {
        label: params.value,
        style: {
          backgroundColor: "#949704",
          color: COLORS.adminAppTextSecondary,
          border: "none",
          width: "100px",
        },
      }
    } else {
      return {
        label: params.value,
        style: {
          backgroundColor: COLORS.darkGreen,
          color: COLORS.adminAppTextSecondary,
          border: "none",
          width: "100px",
        },
      }
    }
  }

  const onClickCounterCard = (key: string, value?: Array<string>) => {
    const prevFilters = calculateToFromFilter(AppStore.counterCardFilter[infiniteTableKey])
    let tempFilter: filterInt = {
      ...filterRef.current,
      [key]: value,
      ...prevFilters,
    }
    let tempValRef: any = filterRef.current
    tempValRef = tempValRef?.[key] || []
    if (filterRef.current.hasOwnProperty(key) && tempValRef.join("") === value?.join("")) {
      delete tempFilter[key]
    }
    filterRef.current = { ...tempFilter }
    resetQuery(arrDeptRest)
  }

  const onChangeRangeFilter = (filterData: TAPIPayload) => {
    const prevFilters = { ...filterRef.current }
    filterRef.current = { ...prevFilters, ...filterData }
    resetQuery(arrDeptRest)
    refetchAnDCounter()
  }

  const onChangeAdvFilter = (filterData: any) => {
    const prevFilters = calculateToFromFilter(AppStore.counterCardFilter[infiniteTableKey])
    filterRef.current = { ...prevFilters, ...filterData }
    resetQuery(arrDeptRest)
    refetchAnDCounter()
    setFilterPopper(false)
  }

  const onClickOpenFilters = (event: React.MouseEvent<HTMLElement>) => {
    setFilterPopper(true)
  }

  const onClickCloseFilters = (event: React.MouseEvent<HTMLElement>, reason: "backdropClick" | "escapeKeyDown" | "manual") => {
    setFilterPopper(false)
  }

  const onChangeInactiveTickets = useCallback((value: boolean) => {
    AppStore.setIsHideInactiveTickets(infiniteTableKey, value)
    resetQuery(arrDeptRest)
    refetchAnDCounter()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      <>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "1rem",
            mb: "25px",
          }}
        >
          <Image
            src={HomeIcon.src}
            alt="HomeIcon"
            width={21.45}
            height={24}
            style={{
              filter: "invert(50%) brightness(100)",
            }}
          />
          <Typography sx={{ fontSize: "1.5rem", fontWeight: "600" }}>Arrival & Departure</Typography>
        </Box>
        <Box
          sx={{
            display: "grid",
            gap: "24px",
            gridTemplateColumns: "repeat(3, 1fr)",
            position: "relative",
          }}
        >
          <CounterCardFilter moduleName={ModuleName.AND} onChangeRangeFilter={onChangeRangeFilter} />
          <>
            <CounterCards
              bgImage={ManSitting.src}
              title={"Arrival Forms"}
              requestCount={andCounter?.data?.arrival_forms ?? 0}
              // @ts-ignore
              selected={filterRef.current["services_type"]?.includes("arrival")}
              onCountClick={() => onClickCounterCard("services_type", ["arrival"])}
            />
            <CounterCards
              bgImage={BooksSpread.src}
              title={"Departure Forms"}
              requestCount={andCounter?.data?.departure_forms ?? 0}
              // @ts-ignore
              selected={filterRef.current["services_type"]?.includes("departure")}
              onCountClick={() => onClickCounterCard("services_type", ["departure"])}
            />
            <CounterCards
              bgImage={Calculator.src}
              title={"Total Forms"}
              requestCount={andCounter?.data?.total_forms ?? 0}
              selected={false}
              onCountClick={() => onClickCounterCard("services_type", undefined)}
            />
          </>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            border: `2px solid #3CA63C`,
            borderRadius: "10px",
            mt: 4,
            height: "100%",
            minHeight: "580px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              // background: "#3E433B", //
              background: COLORS.adminAppBgColorV2,
              borderRadius: "10px 10px 0 0",
              height: "80px",
            }}
          >
            <Box sx={{ padding: "20px 30px", display: "block" }}>
              <Button
                ref={filterAnchorDebugref}
                sx={{
                  position: "relative",
                  width: "125px",
                  height: "40px",
                  borderRadius: "5px",
                  background: "rgba(255, 255, 255, 0.15)",
                  color: "#fff",
                  fontFamily: "Inter",
                  fontSize: "13px",
                  fontWeight: 600,
                  border: "2px solid transparent",
                  borderColor:
                    AppStore.advancedFilter[infiniteTableKey][0]["range"] || AppStore.advancedFilter[infiniteTableKey][0]["value"] !== ""
                      ? "#3CA63C"
                      : undefined,
                  "&:hover": {
                    background: "rgba(255, 255, 255, 0.15)",
                  },
                  "& .MuiButton-startIcon": {
                    marginRight: "5px",
                  },
                  "& .MuiButton-endIcon": {
                    marginLeft: "5px",
                  },
                }}
                startIcon={<Image src={FilterIcon} alt="F" width={18} height={18} />}
                endIcon={<Image src={FilterDDIcon} alt="F" width={18} height={18} />}
                onClick={onClickOpenFilters}
                disabled={!isFetchedProperty || !isFetchedEmployees}
              >
                Filters
                {(AppStore.advancedFilter[infiniteTableKey][0]["range"] || AppStore.advancedFilter[infiniteTableKey][0]["value"] !== "") && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: -10,
                      right: -10, //
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      width: "24px",
                      height: "24px",
                      borderRadius: "50%",
                      background: "#3CA63C",
                      zIndex: 1,
                    }}
                  >
                    {Object.keys(toJS(AppStore.advancedFilter[infiniteTableKey])).length}
                  </Box>
                )}
              </Button>
              <FilterPopover
                // open
                open={filterPopper}
                anchorEl={filterAnchorDebugref.current}
                columns={columns}
                sortBy="headerName"
                onClose={onClickCloseFilters}
                moduleName={infiniteTableKey}
                onChangeAdvFilter={onChangeAdvFilter}
              />
            </Box>
            <FormControlLabel
              control={<Checkbox sx={{ color: "white" }} />}
              label="Hide inactive tickets"
              checked={AppStore.isHideInactiveTickets[infiniteTableKey].isTrue}
              onChange={() => onChangeInactiveTickets(!AppStore.isHideInactiveTickets[infiniteTableKey].isTrue)}
            />
          </Box>
          <InfiniteTable
            infiniteTableKey={infiniteTableKey}
            totalCount={totalNumberOfTickets}
            rows={arrDeptTicketData?.pages?.[arrDeptTicketData?.pages?.length - 1]?.data?.data?.items?.flat() || []}
            columns={columns} // @ts-ignore
            reactQuery={arrDeptRest}
            hiddenColumns={{ owner_stays: false, guest_issue: false, arrival_date: false, departure_date: false, priority: false }}
            refs={{
              isNextPage,
              filterRef,
              sortRef,
              tableAPIRef,
            }}
            mapper={{
              sortIdMap,
            }}
          />
        </Box>
      </>
    </>
  )
}

export default Listing
